import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { PublicAuditResults } from "@/components/audit/PublicAuditResults";
import { getPublicAuditRun } from "@/lib/audit.functions";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";

function publicAuditQueryOptions(slug: string, locale: "en" | "es") {
  return queryOptions({
    queryKey: ["publicAudit", locale, slug],
    queryFn: () => getPublicAuditRun({ data: { slug, locale } }),
  });
}

async function loadBoth(slug: string) {
  const [en, es] = await Promise.all([
    getPublicAuditRun({ data: { slug, locale: "en" } }),
    getPublicAuditRun({ data: { slug, locale: "es" } }),
  ]);
  return { en, es };
}

export const Route = createFileRoute("/es/auditoria/resultados/$slug")({
  loader: async ({ params, context }) => {
    const both = await loadBoth(params.slug);
    if (!both.en && !both.es) throw notFound();
    if (!both.es) throw notFound();
    await context.queryClient.ensureQueryData(publicAuditQueryOptions(params.slug, "es"));
    return { siblingAvailable: Boolean(both.en) };
  },
  head: ({ params, loaderData }) => {
    const slug = params.slug;
    const hostDisplay = slug.replace(/-/g, ".");
    const title = `${hostDisplay}: Auditoría Shopify gratis · Shugert`;
    const description = `Auditoría gratis de la tienda Shopify ${hostDisplay}: rendimiento, SEO, CRO y AEO (legibilidad por IA) por Shugert.`;
    const path = `/es/auditoria/resultados/${slug}`;
    return buildHead({
      noindex: true,
      locale: "es",
      title,
      description,
      path,
      // Same reasoning as the English audit-results route: only pair
      // hreflang with a genuine English sibling report. Falling back to the
      // generic /audit page created a one-way hreflang link nothing
      // reciprocated. Self-referencing suppresses the pair via buildHead's
      // enHref !== esHref check.
      altPath: loaderData?.siblingAvailable ? `/audit/results/${slug}` : path,
      jsonLd: breadcrumbJsonLd([
        { name: "Inicio", path: "/es" },
        { name: "Auditoría gratis", path: "/es/auditoria" },
        { name: hostDisplay, path },
      ]),
    });
  },
  errorComponent: ({ reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold">Algo salió mal</h1>
          <p className="mt-3 text-muted-foreground">No pudimos cargar esta auditoría en este momento.</p>
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="mt-6 inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground/40"
          >
            Reintentar
          </button>
        </div>
      </SiteShell>
    );
  },
  notFoundComponent: () => {
    const params = Route.useParams();
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-6 py-24 text-center">
          <h1 className="font-display text-3xl font-semibold">No hay auditoría para esta tienda</h1>
          <p className="mt-3 text-muted-foreground">
            No tenemos una auditoría pública para <span className="font-mono">{params.slug.replace(/-/g, ".")}</span> aún.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="/es/auditoria"
              className="inline-flex rounded-lg bg-foreground px-4 py-2 text-sm font-semibold text-background hover:opacity-90"
            >
              Correr una auditoría gratis
            </a>
            <a
              href={`/audit/results/${params.slug}`}
              className="inline-flex rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:border-foreground/40"
            >
              View in English
            </a>
          </div>
        </div>
      </SiteShell>
    );
  },
  component: function AuditResultsPageEs() {
    const params = Route.useParams();
    const { siblingAvailable } = Route.useLoaderData();
    const { data } = useSuspenseQuery(publicAuditQueryOptions(params.slug, "es"));
    if (!data) throw notFound();
    return (
      <SiteShell>
        <PublicAuditResults run={data} locale="es" siblingLocaleAvailable={siblingAvailable} />
      </SiteShell>
    );
  },
});